import type { CategoryFilterSectionProps } from './CategoryFilterSection';
import FilterBarFilters from '@modules/search/components/search-filter-section/FilterBarFilters';
import { Link } from '@remix-run/react';
import { useMemo } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { trackBreadcrumbClick } from '~/modules/tracking/util/trackBreadcrumbClick';
import { SortDropdown } from '@rocco/ui/filter-sort/components/SortDropdown';
import type { FilterSortOption } from '@rocco/ui/filter-sort/components/Filters';
import type { SortOption } from '@rocco/types/enum/sort-option';
import type { SearchSortOptionResult } from '@modules/search/hook/use-search-sort-options';
import { useSearchSortOptions } from '~/modules/search/hook/use-search-sort-options';
import SearchSort from '@modules/search/components/search-filter-section/RoccoBasedSearchSort';

export const CategoryFilterSection = ({
    category,
    componentBefore,
    componentBarAddon,
    breadcrumbs,
    searchFilters,
    sortOptions,
    updateSort,
}: CategoryFilterSectionProps) => {
    const tracker = useTracker();
    const internalSortOptions = useSearchSortOptions({
        category,
        sortBy: searchFilters.filterStates.sortBy,
        defaultSortBy: searchFilters.filterStates.defaultSortBy,
    });
    const allBreadcrumbs = useMemo(
        () =>
            breadcrumbs?.length
                ? [{ href: '/', label: 'Home' }, ...breadcrumbs]
                : null,
        [breadcrumbs],
    );

    return (
        <div className="px-12.5 pb-6">
            {/* Filter controls (left) and component addons (right) - above the line */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {/* If caller provided componentBefore, render it; otherwise render the FilterBarFilters (original behaviour) */}
                    {componentBefore ? (
                        componentBefore
                    ) : (
                        <FilterBarFilters
                            filters={searchFilters.filterBarFilters ?? []}
                            updateFilter={searchFilters.updateFilter}
                            componentBarAddon={componentBarAddon}
                            filterStates={searchFilters.filterStates}
                        />
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {componentBarAddon}
                </div>
            </div>
            <div
                className="my-6 h-px"
                style={{ backgroundColor: 'rgba(20,20,20,0.2)' }}
            />
            {/* Breadcrumbs (below Category Pills) and Sort By (below Toggle) */}
            <div className="mt-6 flex items-center justify-between">
                {/* Breadcrumbs below Category Pills on the left */}
                {allBreadcrumbs && (
                    <div className="flex items-center gap-3">
                        {allBreadcrumbs.map((breadcrumb, index) => (
                            <span
                                key={breadcrumb.href}
                                className="inline-flex items-center gap-2"
                            >
                                <Link
                                    to={breadcrumb.href}
                                    onClick={() =>
                                        trackBreadcrumbClick(
                                            tracker,
                                            breadcrumb.href,
                                        )
                                    }
                                >
                                    <span className="text-[1.25rem] font-medium leading-[130%] tracking-[0.4] text-[#141414] hover:text-gray-700">
                                        {breadcrumb.label}
                                    </span>
                                </Link>
                                {index !== allBreadcrumbs.length - 1 && (
                                    <span className="text-[1.25rem] font-medium leading-[130%] tracking-[0.4] text-[#141414]">
                                        /
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                )}
                {/* Sort By below Toggle on the right */}
                {sortOptions ? (
                    <SortByBelowLine
                        sortOptions={sortOptions}
                        updateSort={updateSort}
                    />
                ) : (
                    internalSortOptions.items.length > 0 && (
                        <SearchSort
                            optionResult={
                                internalSortOptions as SearchSortOptionResult
                            }
                        />
                    )
                )}
            </div>
        </div>
    );
};

// Component to render Sort By below the border line
const SortByBelowLine = ({
    sortOptions,
    updateSort,
}: {
    sortOptions?: FilterSortOption[];
    updateSort?: (sortOption: SortOption) => void;
}) => {
    if (!sortOptions || sortOptions.length === 0) {
        return null;
    }
    return <SortDropdown sortOptions={sortOptions} updateSort={updateSort} />;
};
